import {
  situacaoComprador,
  formatarDinheiro,
  formatarDataMinuto,
  linkWhatsAppMensagem,
  podeGerarConvite,
  motivoConviteIndisponivel,
} from '../utils';
import type { InscritoGerenciar } from '../types';

interface Props {
  compradores: InscritoGerenciar[];
  eventoNome: string;
  aoAbrir: (inscrito: InscritoGerenciar) => void;
  aoGerarConvite: (inscrito: InscritoGerenciar) => void;
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}

export default function ListaCompradores({ compradores, eventoNome, aoAbrir, aoGerarConvite }: Props) {
  return (
    <div className="space-y-3">
      {compradores.map((c) => {
        const s = situacaoComprador(c.status);
        const telefone = (c.participante_telefone ?? '').replace(/\D/g, '');
        const temWhatsApp = telefone.length > 0;
        const mensagem = `Olá, ${primeiroNome(c.participante)}! Aqui é da organização do ${eventoNome}. `;
        const href = temWhatsApp ? linkWhatsAppMensagem(c.participante_telefone!, mensagem) : '#';
        const cancelada = c.status === 'cancelada';
        const podeConvite = podeGerarConvite(c.status);
        const tituloConvite = podeConvite ? 'Gerar convite' : motivoConviteIndisponivel(c.status);
        const meta = [
          c.modalidade,
          c.grupo ?? null,
          formatarDataMinuto(c.criado_em),
          formatarDinheiro(c.valor_centavos),
        ]
          .filter(Boolean)
          .join(' · ');

        return (
          <div
            key={c.inscricao_id}
            className="flex items-start gap-3 rounded-3xl bg-white p-3 shadow-[0_6px_18px_rgba(20,25,60,.06)]"
          >
            <button
              type="button"
              onClick={() => aoAbrir(c)}
              className="min-w-0 flex-1 cursor-pointer text-left"
            >
              <span
                className={`block truncate text-[16px] font-semibold leading-snug ${
                  cancelada ? 'text-foreground-400' : 'text-foreground-950'
                }`}
              >
                {c.participante}
              </span>
              <span className="mt-0.5 block truncate text-[13px] text-foreground-500">{meta}</span>
              <span className="mt-2 inline-flex">
                <span
                  className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${s.cls}`}
                >
                  {s.rotulo}
                </span>
              </span>
            </button>

            <div className="flex shrink-0 items-center gap-2">
              {temWhatsApp ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Chamar ${c.participante} no WhatsApp`}
                  title="Chamar no WhatsApp"
                  className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#25D366] text-white transition-opacity hover:opacity-90"
                >
                  <i className="ri-whatsapp-line text-xl" />
                </a>
              ) : (
                <span
                  title="sem WhatsApp"
                  className="flex h-10 w-10 shrink-0 cursor-default items-center justify-center rounded-full bg-secondary-100 text-secondary-400"
                >
                  <i className="ri-whatsapp-line text-xl" />
                </span>
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (podeConvite) aoGerarConvite(c);
                }}
                disabled={!podeConvite}
                aria-label={tituloConvite}
                title={tituloConvite}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors ${
                  podeConvite
                    ? 'cursor-pointer border-secondary-300 bg-white text-foreground-700 hover:bg-background-100'
                    : 'cursor-not-allowed border-secondary-200 bg-secondary-100 text-secondary-400 opacity-60'
                }`}
              >
                <i className="ri-qr-code-line text-xl" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}